import { useEffect, useState } from "react";

/**
 * Hook : fetch la latest release du repo cockpit sur GitHub et expose
 * l'asset binaire le plus adapte a l'OS courant.
 *
 * - 60 req/h anonyme via api.github.com — largement assez pour les visites
 *   sur dozka.app, on cache 5 min en sessionStorage.
 * - Si l'API rate (offline, rate-limited, 404), on tombe sur le fallback
 *   `releases/latest` HTML page generique.
 * - Detection OS via navigator.userAgentData (Chromium) ou UA-sniff sinon.
 *   Detection arch best-effort : sur Mac on tape direct le universal binary
 *   donc l'arch ne change rien. Sur Windows/Linux on sert le x64 par defaut
 *   (les users arm peuvent toujours cliquer sur "Toutes les versions").
 */

// Repo public dedie aux releases (cf cockpit/docs/distribution-roadmap.md).
// Le code source vit dans httpbratz/dozka-cockpit (prive), les binaires
// sont mirror ici pour download anonyme.
const REPO_OWNER = "xeylabot";
const REPO_NAME = "dozka-cockpit-releases";
const CACHE_KEY = "dozka-latest-release-v1";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

const RELEASES_PAGE_URL = `https://github.com/${REPO_OWNER}/${REPO_NAME}/releases/latest`;
const API_URL = `https://api.github.com/repos/${REPO_OWNER}/${REPO_NAME}/releases/latest`;

export type DetectedOS = "windows" | "macos" | "linux" | "unknown";

interface GitHubAsset {
  name: string;
  browser_download_url: string;
  size: number;
}

interface CachedRelease {
  fetchedAt: number;
  tagName: string;
  assets: GitHubAsset[];
}

export interface LatestReleaseResult {
  loading: boolean;
  os: DetectedOS;
  /** URL directe vers le binaire approprie, ou null si pas trouve. */
  directUrl: string | null;
  /** Nom du fichier (ex: "Dozka.Cockpit_0.1.15_universal.dmg"). */
  filename: string | null;
  /** Tag de la version (ex: "v0.1.15"). */
  version: string | null;
  /** Toujours dispo en fallback. */
  releasesPageUrl: string;
}

function detectOS(): DetectedOS {
  if (typeof navigator === "undefined") return "unknown";

  // Chromium-only API, mais c'est l'OS hint le plus fiable.
  const uaData = (navigator as Navigator & {
    userAgentData?: { platform?: string };
  }).userAgentData;
  if (uaData?.platform) {
    const p = uaData.platform.toLowerCase();
    if (p.includes("win")) return "windows";
    if (p.includes("mac")) return "macos";
    if (p.includes("linux")) return "linux";
  }

  // Fallback UA-sniff pour Safari / Firefox.
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return "windows";
  if (/Mac OS X|Macintosh/i.test(ua)) return "macos";
  if (/Linux|X11/i.test(ua)) return "linux";
  return "unknown";
}

function pickAssetForOS(assets: GitHubAsset[], os: DetectedOS): GitHubAsset | null {
  // Priorites par OS — patterns tauri-action V0 :
  //   macOS : *.dmg (universal) preferred, sinon *_aarch64.dmg ou *_x64.dmg
  //   Windows : *_x64-setup.exe (NSIS) preferred, sinon *.msi
  //   Linux : *.AppImage (universel) preferred, sinon *_amd64.deb
  const matchers: Record<DetectedOS, RegExp[]> = {
    macos: [/universal\.dmg$/i, /\.dmg$/i],
    windows: [/_x64-setup\.exe$/i, /-setup\.exe$/i, /\.exe$/i, /\.msi$/i],
    linux: [/\.AppImage$/i, /\.deb$/i],
    unknown: [],
  };
  for (const rx of matchers[os]) {
    const hit = assets.find((a) => rx.test(a.name));
    if (hit) return hit;
  }
  return null;
}

function readCache(): CachedRelease | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedRelease;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(data: CachedRelease) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage peut etre disabled (private mode), on n'a pas de cache, tant pis.
  }
}

export function useLatestRelease(): LatestReleaseResult {
  const [state, setState] = useState<{
    loading: boolean;
    tagName: string | null;
    assets: GitHubAsset[];
  }>(() => {
    const cached = readCache();
    return {
      loading: !cached,
      tagName: cached?.tagName ?? null,
      assets: cached?.assets ?? [],
    };
  });

  const os = detectOS();

  useEffect(() => {
    // Si on a deja un cache valide, skip le fetch.
    if (state.tagName) return;

    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(API_URL, {
          headers: { Accept: "application/vnd.github+json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as {
          tag_name: string;
          assets: GitHubAsset[];
        };
        const next: CachedRelease = {
          fetchedAt: Date.now(),
          tagName: data.tag_name,
          assets: data.assets,
        };
        writeCache(next);
        if (!cancelled) {
          setState({
            loading: false,
            tagName: data.tag_name,
            assets: data.assets,
          });
        }
      } catch (e) {
        if (!cancelled) {
          // Fallback silencieux : on garde tagName=null, le caller utilisera
          // releasesPageUrl. La console.debug est utile pour debug.
          console.debug("[latestRelease] fetch failed:", e);
          setState({ loading: false, tagName: null, assets: [] });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.tagName]);

  const picked = pickAssetForOS(state.assets, os);

  return {
    loading: state.loading,
    os,
    directUrl: picked?.browser_download_url ?? null,
    filename: picked?.name ?? null,
    version: state.tagName,
    releasesPageUrl: RELEASES_PAGE_URL,
  };
}
